import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { StatCard } from '../../components/ui/stat-card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../components/ui/use-toast';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Filler, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { LayoutDashboard, Bell, AlertCircle, Settings, Wrench, AlertTriangle, Activity, Monitor, Cpu, FlaskConical, Target } from "lucide-react";
import { PageHeader } from '@/components/ui/page-header';
import { useDeviceData } from '../../hooks/useApiData';
import { useLabLinkDashboard } from '../../hooks/useLabLinkDashboard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, ChartTooltip, Legend);

export const DeviceMonitoringDashboard: React.FC = () => {
  const { toast } = useToast();
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedDevice, setSelectedDevice] = useState('all');

  const { connectors, analyzers, error: deviceError } = useDeviceData();
  const { data: dashboardData } = useLabLinkDashboard(60000);

  const stats = useMemo(() => {
    const totalConnectors = connectors.length;
    const connectorsOnline = connectors.filter((c: any) => c.is_online).length;
    const connectorOnlineMap = new Set(connectors.filter((c: any) => c.is_online).map((c: any) => c.id));
    const totalAnalyzers = analyzers.length;
    const analyzersOnline = analyzers.filter((a: any) => a.status === 'active' && a.connector_device && connectorOnlineMap.has(a.connector_device)).length;
    const apiStats = dashboardData?.statistics;
    const uptimeNumeric = apiStats?.system_uptime ? parseFloat(apiStats.system_uptime.replace('%', '')) : 0;
    return {
      system_uptime: uptimeNumeric || 0,
      connectors_online: apiStats?.active_connectors ?? connectorsOnline,
      total_connectors: apiStats?.total_connectors ?? totalConnectors,
      analyzers_online: apiStats?.active_analyzers ?? analyzersOnline,
      total_analyzers: apiStats?.total_analyzers ?? totalAnalyzers,
      tests_today: apiStats?.test_results_today ?? 0,
      qc_compliance: apiStats?.average_quality_score ?? 0,
      active_alerts: apiStats?.unacknowledged_alerts ?? 0,
      critical_alerts: apiStats?.critical_alerts ?? 0,
    };
  }, [connectors, analyzers, dashboardData]);

  const alerts = dashboardData?.recent_alerts || [];
  const [alertsState, setAlertsState] = useState<any[]>([]);
  useEffect(() => { setAlertsState(alerts); }, [alerts]);

  const authToken = useMemo(() => localStorage.getItem('access') || localStorage.getItem('access_token') || sessionStorage.getItem('access') || '', []);

  const handleResolve = async (alert: any) => {
    if (!alert?.id) return;
    try {
      const headers: any = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      let resp = await fetch(`/v1/lablink/api/alerts/${alert.id}/`, { method: 'PATCH', headers, body: JSON.stringify({ status: 'resolved' }) });
      if (!resp.ok) resp = await fetch(`/v1/lablink/api/alerts/${alert.id}/resolve/`, { method: 'POST', headers });
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      setAlertsState(prev => prev.filter(a => a.id !== alert.id));
      toast({ title: 'Alert resolved' });
    } catch (e: any) {
      toast({ variant: 'destructive', title: `Resolve failed: ${e.message}` });
    }
  };

  const uptimeData = [
    { time: '00:00', uptime: 99.2 }, { time: '04:00', uptime: 98.8 }, { time: '08:00', uptime: 99.5 },
    { time: '12:00', uptime: 97.2 }, { time: '16:00', uptime: 99.1 }, { time: '20:00', uptime: 99.8 }, { time: '24:00', uptime: 99.3 },
  ];
  const throughputData = [
    { time: '00:00', tests: 45 }, { time: '02:00', tests: 23 }, { time: '04:00', tests: 12 }, { time: '06:00', tests: 67 },
    { time: '08:00', tests: 156 }, { time: '10:00', tests: 234 }, { time: '12:00', tests: 287 }, { time: '14:00', tests: 298 },
    { time: '16:00', tests: 245 }, { time: '18:00', tests: 189 }, { time: '20:00', tests: 134 }, { time: '22:00', tests: 89 },
  ];
  // Chart.js paints to canvas, where CSS var() never resolves — read the
  // design tokens off the root at render time so charts follow the brand
  // and the clinical roles instead of a private palette.
  const token = (name: string, alpha?: number) => {
    const t = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return alpha != null ? `hsl(${t} / ${alpha})` : `hsl(${t})`;
  };
  const deviceStatusData = useMemo(() => [
    { name: 'Online', value: stats.analyzers_online, color: token('--success') },
    { name: 'Offline', value: Math.max(stats.total_analyzers - stats.analyzers_online, 0), color: token('--destructive') },
    { name: 'Maintenance', value: analyzers.filter((a: any) => a.status === 'maintenance').length, color: token('--warning') },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [stats, analyzers]);

  const getSeverityColor = (s: string) => s === 'critical' ? 'text-destructive-700' : s === 'high' ? 'text-warning-700' : s === 'medium' ? 'text-warning-700' : 'text-info';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Device Monitoring Dashboard"
        icon={<LayoutDashboard className="h-6 w-6" />}
        actions={
          <div className="flex items-center gap-2">
            <Select value={timeRange} onValueChange={setTimeRange}><SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="1h">Last Hour</SelectItem><SelectItem value="24h">Last 24h</SelectItem><SelectItem value="7d">Last 7 days</SelectItem><SelectItem value="30d">Last 30 days</SelectItem></SelectContent></Select>
            <Select value={selectedDevice} onValueChange={setSelectedDevice}><SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Devices</SelectItem></SelectContent></Select>
            <Button variant="outline"><Bell className="h-4 w-4 mr-1" />View All Alerts</Button>
          </div>
        }
      />

      {deviceError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>Unable to fetch device data: {deviceError}</AlertDescription></Alert>}

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="System Uptime" value={`${stats.system_uptime}%`} icon={Activity} tone="success" foot={<Progress value={stats.system_uptime} className="h-1.5" />} />
        <StatCard label="Connectors" value={<>{stats.connectors_online}<span className="text-sm text-muted-foreground font-normal">/ {stats.total_connectors}</span></>} icon={Monitor} tone="brand" />
        <StatCard label="Analyzers" value={<>{stats.analyzers_online}<span className="text-sm text-muted-foreground font-normal">/ {stats.total_analyzers}</span></>} icon={Cpu} tone="brand" />
        <StatCard label="Tests Today" value={stats.tests_today} icon={FlaskConical} tone="brand" />
        <StatCard label="Quality Score" value={`${stats.qc_compliance}%`} icon={Target} tone="success" />
        <StatCard label="Active Alerts" value={stats.active_alerts} icon={AlertTriangle} tone={stats.active_alerts > 0 ? "destructive" : "success"} />
      </div>

      {(stats.active_alerts > 0 || stats.critical_alerts > 0) && (
        <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>Unacknowledged: {stats.active_alerts} | Critical: {stats.critical_alerts}. Click "View All Alerts" for details.</AlertDescription></Alert>
      )}

      {/* Charts + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="uptime">
            <TabsList><TabsTrigger value="uptime">System Uptime</TabsTrigger><TabsTrigger value="throughput">Test Throughput</TabsTrigger><TabsTrigger value="status">Device Status</TabsTrigger></TabsList>
            <TabsContent value="uptime">
              <Card><CardHeader><CardTitle>Device Uptime Over Time</CardTitle></CardHeader><CardContent>
                <div style={{ height: 300 }}>
                  <Line data={{ labels: uptimeData.map(d => d.time), datasets: [{ label: 'Uptime %', data: uptimeData.map(d => d.uptime), borderColor: token('--success'), backgroundColor: token('--success', 0.3), fill: true, tension: 0.4 }] }} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { min: 95, max: 100 } }, plugins: { legend: { display: false } } }} />
                </div>
              </CardContent></Card>
            </TabsContent>
            <TabsContent value="throughput">
              <Card><CardHeader><CardTitle>Test Throughput (Tests/Hour)</CardTitle></CardHeader><CardContent>
                <div style={{ height: 300 }}>
                  <Bar data={{ labels: throughputData.map(d => d.time), datasets: [{ label: 'Tests', data: throughputData.map(d => d.tests), backgroundColor: token('--primary') }] }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                </div>
              </CardContent></Card>
            </TabsContent>
            <TabsContent value="status">
              <Card><CardHeader><CardTitle>Device Status Distribution</CardTitle></CardHeader><CardContent>
                <div style={{ height: 300 }}>
                  <Pie data={{ labels: deviceStatusData.map(d => d.name), datasets: [{ data: deviceStatusData.map(d => d.value), backgroundColor: deviceStatusData.map(d => d.color) }] }} options={{ responsive: true, maintainAspectRatio: false }} />
                </div>
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Alerts */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Bell className="h-4 w-4" />Recent Alerts <Badge variant="error" className="ml-1">{alertsState.length}</Badge></CardTitle></CardHeader>
            <CardContent>
              {alertsState.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No active alerts</p> : (
                <div className="space-y-3">
                  {alertsState.slice(0, 5).map((alert: any, i: number) => (
                    <div key={alert.id || i} className="flex items-start gap-2 text-xs">
                      <AlertCircle className={`h-4 w-4 mt-0.5 shrink-0 ${getSeverityColor(alert.severity)}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{alert.device_name || 'Alert'}</div>
                        <div className="text-muted-foreground truncate">{alert.message}</div>
                        <div className="text-[10px] text-muted-foreground">{alert.time_ago || (alert.created_at && new Date(alert.created_at).toLocaleTimeString())}</div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] shrink-0" onClick={() => handleResolve(alert)}>Resolve</Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Device Health */}
          <Card>
            <CardHeader><CardTitle className="text-base">Device Health Summary</CardTitle></CardHeader>
            <CardContent>
              {analyzers.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No devices</p> : (
                <div className="space-y-3">
                  {analyzers.slice(0, 3).map((a: any, i: number) => {
                    const scores = [98, 85, 65, 92, 78, 88];
                    const score = scores[i % scores.length];
                    const color = score >= 95 ? 'text-success-700' : score >= 80 ? 'text-warning-700' : 'text-destructive-700';
                    return (
                      <div key={a.id}>
                        <div className="flex justify-between text-xs mb-1">
                          <span>{a.analyzer_name || `${a.manufacturer || ''} ${a.model || ''}`.trim() || `Analyzer ${String(a.id).slice(0, 8)}`}</span>
                          <span className={color}>{score >= 95 ? 'Excellent' : score >= 80 ? 'Good' : 'Needs Attention'}</span>
                        </div>
                        <Progress value={score} className={`h-1.5 ${score < 80 ? '[&>div]:bg-destructive' : score < 95 ? '[&>div]:bg-warning' : ''}`} />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardHeader><CardTitle className="text-base">Recent Activity</CardTitle></CardHeader>
            <CardContent>
              {connectors.length === 0 && analyzers.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No activity</p> : (
                <div className="space-y-3">
                  {connectors.concat(analyzers).slice(0, 4).map((device: any, i: number) => {
                    const activities = [
                      { text: 'came online', color: 'bg-success', time: '2 minutes ago' },
                      { text: 'maintenance scheduled', color: 'bg-warning', time: '15 minutes ago' },
                      { text: 'alert generated', color: 'bg-destructive', time: '1 hour ago' },
                      { text: 'status updated', color: 'bg-info', time: '3 hours ago' },
                    ];
                    const act = activities[i % activities.length];
                    const name = device.name || device.analyzer_name || `Device ${String(device.id).slice(0, 8)}`;
                    return (
                      <div key={device.id} className="flex items-start gap-2 text-xs">
                        <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${act.color}`} />
                        <div><div>{name} {act.text}</div><div className="text-muted-foreground">{act.time}</div></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Connector & Analyzer details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Wrench className="h-4 w-4" />LabLink Connectors Status</CardTitle></CardHeader>
          <CardContent>
            {connectors.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No connectors found</p> : (
              <div className="divide-y">
                {connectors.slice(0, 5).map((c: any) => (
                  <div key={c.id} className="flex items-center justify-between py-2">
                    <div><div className="font-medium text-sm">{c.name || `Connector ${String(c.id).slice(0, 8)}`}</div><div className="text-xs text-muted-foreground">{c.location_description || 'Location not set'}</div></div>
                    <div className="text-right"><Badge variant={c.is_online ? 'success' : 'error'}>{c.is_online ? 'Online' : 'Offline'}</Badge><div className="text-xs text-muted-foreground">{c.analyzer_count || 0} analyzers</div></div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Settings className="h-4 w-4" />Analyzer Performance</CardTitle></CardHeader>
          <CardContent>
            {analyzers.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No analyzers found</p> : (
              <div className="divide-y">
                {analyzers.slice(0, 5).map((a: any) => (
                  <div key={a.id} className="flex items-center justify-between py-2">
                    <div><div className="font-medium text-sm">{a.name || a.analyzer_name || `${a.manufacturer || ''} ${a.model || ''}`.trim()}</div><div className="text-xs text-muted-foreground">{a.category_display || a.category}</div></div>
                    <div className="text-right"><Badge variant={a.status === 'active' ? 'success' : a.status === 'maintenance' ? 'warning' : 'error'}>{a.status_display || a.status}</Badge><div className="text-xs text-muted-foreground">{(a.total_tests_processed ?? 0).toLocaleString()} tests</div></div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DeviceMonitoringDashboard;
